/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["_bodyElement", "children", "className", "Content", "defaultOpen", "delay", "delayOnHold", "disabled", "headingText", "innerClassName", "maxWidth", "onOpenChange", "open", "style", "target", "title", "use"];
import classNames from 'classnames';
import { Children, Fragment, cloneElement, useCallback, useEffect, useRef, useState, memo } from 'react';
import { findDOMNode } from '../utils/findDOMNode';
import { isFragment } from 'react-is';
import devLogger from 'react-utils/devLogger';
import useMounted from 'react-utils/hooks/useMounted';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import { TOOLTIP_DELAY, TOOLTIP_DELAY_ON_HOLD } from 'HubStyleTokens/times';
import { memoizedSequence } from '../core/Functions';
import SyntheticEvent from '../core/SyntheticEvent';
import useControllable from '../hooks/useControllable';
import { isPageInKeyboardMode } from '../listeners/focusStylesListener';
import { getDefaultNode } from '../utils/RootNode';
import UIControlledPopover from './UIControlledPopover';
import UITooltipContent from './UITooltipContent';
import UITooltipHeader from './UITooltipHeader';
import { isCursorOver, shouldTooltipClose } from './utils/Hover';
import ChildrenWrapper from '../utils/ChildrenWrapper';
import { useFoundationsReadinessTracking } from '../utils/useFoundationsReadinessTracking';

// `target` accepts both DOM elements and React ref objects. Refs must be
// dereferenced inside effects (not during render) because the ref's .current
// is null at render time and only populated after React commits the DOM.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const resolveTarget = t => t != null && typeof t === 'object' && 'current' in t ? t.current : t;
const tooltipDelay = parseInt(TOOLTIP_DELAY, 10);
const tooltipDelayOnHold = parseInt(TOOLTIP_DELAY_ON_HOLD, 10);
const USE_CLASSES = {
  default: 'tooltip-default private-tooltip--default',
  danger: 'tooltip-danger private-tooltip--danger',
  longform: 'private-tooltip--longform'
};

/** An array containing every tooltip's handleOpenChange, to allow tooltips to close each other. */
const allTooltipOpeners = [];

/** An empty array to reuse when declaring hooks with no dependencies */
const NO_DEPENDENCIES = [];
const getPopoverUse = use => {
  if (use === 'default') return 'tooltip';
  if (use === 'danger') return 'tooltip-danger';
  return use;
};
const renderTitle = (title, innerClassName, headingText, use) => /*#__PURE__*/_jsxs(UITooltipContent, {
  className: classNames(innerClassName, 'tooltip-inner', use === 'longform' && 'has--vertical-spacing'),
  children: [headingText && /*#__PURE__*/_jsx(UITooltipHeader, {
    children: headingText
  }), title]
});
const UITooltip = /*#__PURE__*/memo(props => {
  const {
      _bodyElement,
      children,
      className,
      Content,
      defaultOpen = false,
      delay = tooltipDelay,
      delayOnHold = tooltipDelayOnHold,
      disabled = false,
      headingText,
      innerClassName,
      maxWidth,
      onOpenChange,
      open,
      style,
      target,
      title,
      use = 'default'
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  useFoundationsReadinessTracking();

  // The element that the Tether is attached to
  const [targetElement, setTargetElement] = useState(() => resolveTarget(target));
  const {
    ownerDocument
  } = _bodyElement || getDefaultNode();
  const [controllableOpen, handleOpenChange] = useControllable(open, defaultOpen, onOpenChange);
  const computedDisabled = disabled || Content == null && !title;
  const computedOpen = controllableOpen && !computedDisabled && !!targetElement;
  const hasOpenedRef = useRef(computedOpen);
  hasOpenedRef.current = hasOpenedRef.current || computedOpen;
  const hasOpened = hasOpenedRef.current;
  const tooltipId = useUniqueId('tooltip-');
  const triggerOpen = () => {
    handleOpenChange(SyntheticEvent(true));
  };

  /** A timeout that will trigger open after `delay` has elapsed */
  const delayTimeoutRef = useRef(null);

  /** A timeout that will trigger open after `delayOnHold` has elapsed */
  const delayOnHoldTimeoutRef = useRef(null);

  /** Cancel any pending timeouts */
  const clearHoverTimeouts = () => {
    if (delayTimeoutRef.current !== null) {
      clearTimeout(delayTimeoutRef.current);
      delayTimeoutRef.current = null;
    }
    if (delayOnHoldTimeoutRef.current !== null) {
      clearTimeout(delayOnHoldTimeoutRef.current);
      delayOnHoldTimeoutRef.current = null;
    }
  };

  /** Ref to the target element (i.e. the child) */
  const childrenWrapperRef = useRef(null);

  /** Is the keyboard focus within the target or popover element? */
  const hasFocusRef = useRef(false);

  /** Is the mouse within (or near) the target or popover element? */
  const hasMouseRef = useRef(false);

  /** Have we been mounted yet? */
  const mountedRef = useMounted();

  // Tasks to perform on mount/unmount
  useEffect(() => {
    allTooltipOpeners.push(handleOpenChange);
    return () => {
      allTooltipOpeners.splice(allTooltipOpeners.indexOf(handleOpenChange), 1);
      clearHoverTimeouts();
    };
  }, NO_DEPENDENCIES);

  // Tasks to perform on open/close
  useEffect(() => {
    clearHoverTimeouts();
    if (computedOpen) {
      // Tell all other tooltips on the page to close
      allTooltipOpeners.forEach(tooltip => {
        if (tooltip === handleOpenChange) return;
        tooltip(SyntheticEvent(false));
      });

      // Bind window-level events that can trigger close
      const maybeTriggerClose = () => {
        if (computedOpen && !hasFocusRef.current && !hasMouseRef.current) {
          handleOpenChange(SyntheticEvent(false));
        }
      };
      const handleWindowMouseLeave = () => {
        hasMouseRef.current = false;
        maybeTriggerClose();
        clearHoverTimeouts();
      };
      const handleBodyMouseMove = evt => {
        const targetEl = targetElement;
        const tooltipEl = ownerDocument.getElementById(tooltipId);
        if (!targetEl) return;
        const hadMousebefore = hasMouseRef.current;
        const targetArea = targetEl.getBoundingClientRect();
        hasMouseRef.current = tooltipEl ? !shouldTooltipClose(targetArea, tooltipEl.getBoundingClientRect(), evt) : isCursorOver(targetArea, evt);
        if (hadMousebefore) maybeTriggerClose();
      };
      const handleBodyFocus = () => {
        const targetEl = targetElement;
        const tooltipEl = ownerDocument.getElementById(tooltipId);
        if (!targetEl) return;
        const activeElement = ownerDocument.activeElement;
        hasFocusRef.current = isPageInKeyboardMode() && (targetEl.contains(activeElement) || tooltipEl && tooltipEl.contains(activeElement));
        maybeTriggerClose();
      };
      const handleKeyDown = evt => {
        if (evt.key === 'Escape') {
          maybeTriggerClose();
        }
      };
      addEventListener('mouseleave', handleWindowMouseLeave);
      ownerDocument.addEventListener('keydown', handleKeyDown);
      ownerDocument.addEventListener('mousemove', handleBodyMouseMove);
      ownerDocument.addEventListener('focus', handleBodyFocus, true);
      return () => {
        removeEventListener('mouseleave', handleWindowMouseLeave);
        ownerDocument.removeEventListener('keydown', handleKeyDown);
        ownerDocument.removeEventListener('mousemove', handleBodyMouseMove);
        ownerDocument.removeEventListener('focus', handleBodyFocus, true);
      };
    }
    return undefined;
  }, [computedOpen]);

  /** Open on focus if the page is in "keyboard mode" (so, not on click) */
  const handleTargetFocus = useCallback(() => {
    if (isPageInKeyboardMode()) triggerOpen();
  }, [NO_DEPENDENCIES]);

  /** Open after a delay when the target element is hovered */
  const handleTargetMouseMove = useCallback(() => {
    clearTimeout(delayOnHoldTimeoutRef.current);
    delayOnHoldTimeoutRef.current = null;
    hasMouseRef.current = true;

    // Open after `delay` as long as the mouse doesn't leave the target
    if (!delayTimeoutRef.current) {
      delayTimeoutRef.current = setTimeout(triggerOpen, delay);
    }

    // Open after `delayOnHold` (sooner) if the mouse holds still
    delayOnHoldTimeoutRef.current = setTimeout(triggerOpen, delayOnHold);
  }, [delay, delayOnHold]);
  const child = Children.only(children);
  if (process.env.NODE_ENV !== 'production') {
    if (isFragment(child)) {
      devLogger.warn({
        message: `UITooltip: child element cannot be a React.Fragment, and must support passing event handlers through to a DOM element.`,
        key: 'UITooltip: fragment not supported'
      });
    }
  }
  let clonedChild;
  if (!computedDisabled) {
    const childDescribedBy = child.props['aria-describedby'];
    const childOnFocus = child.props.onFocus;
    const childOnMouseMove = child.props.onMouseMove;
    clonedChild = /*#__PURE__*/cloneElement(child, {
      'aria-describedby': computedOpen ? `${childDescribedBy || ''} ${tooltipId}`.trim() : childDescribedBy,
      onFocus: computedOpen ? childOnFocus : memoizedSequence(handleTargetFocus, childOnFocus),
      onMouseMove: computedOpen ? childOnMouseMove : memoizedSequence(handleTargetMouseMove, childOnMouseMove),
      onMouseLeave: memoizedSequence(clearHoverTimeouts, child.props.onMouseLeave),
      tabIndex: 0 // #4949
    });
  }
  const prevTargetRef = useRef(null);
  useEffect(() => {
    if (controllableOpen) {
      const next = resolveTarget(target) || /* eslint-disable-next-line react/no-find-dom-node */
      findDOMNode(childrenWrapperRef.current);
      if (next !== prevTargetRef.current) {
        prevTargetRef.current = next;
        setTargetElement(next);
      }
    }
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ChildrenWrapper, {
      ref: childrenWrapperRef,
      children: clonedChild || child
    }), hasOpened && /*#__PURE__*/_jsx(UIControlledPopover, Object.assign({}, rest, {
      _bodyElement: _bodyElement,
      animateOnMount: mountedRef.current,
      arrowSize: use === 'longform' ? 'medium' : 'small',
      className: classNames("tooltip private-tooltip", className, USE_CLASSES[use]),
      closeOnTargetLeave: true,
      content: Content == null ? renderTitle(title, innerClassName, headingText, use) : null,
      Content: Content,
      id: tooltipId,
      open: computedOpen,
      onOpenChange: handleOpenChange // #8863
      ,
      style: Object.assign({}, style, {
        maxWidth
      }),
      target: targetElement,
      use: getPopoverUse(use)
    }))]
  });
});
UITooltip.displayName = 'UITooltip';
export default UITooltip;